import { capitalize } from "@mui/material"
import { useMutation } from "urql"

import { pluralize } from "../../../../../../utils/stringUtils"

import type { InstructionMutationBatchDeleteProductInstructionsArgs, Mutation } from "@/graphql/codegen/graphql"
import { RemoveInstructionsFromProductDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const useRemoveInstructionsFromProduct = (): {
  removeInstructionsFromProduct: (args: { instructionIds: string[]; productId: number }) => Promise<boolean>
} => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [, removeInstructions] = useMutation<
    Pick<Mutation, "instruction">,
    InstructionMutationBatchDeleteProductInstructionsArgs
  >(RemoveInstructionsFromProductDocument)

  const removeInstructionsFromProduct = async ({
    instructionIds,
    productId,
  }: {
    instructionIds: string[]
    productId: number
  }) => {
    const { error, data } = await removeInstructions({
      input: { productId, instructionIds },
    })

    const name = pluralize(instructionIds.length, "instruction", "instructions")

    const hasError =
      error?.message ||
      (data?.instruction.batchDeleteProductInstructions.__typename === "InstructionFailure" &&
        Boolean(data?.instruction.batchDeleteProductInstructions.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? `Failed to remove ${name} from product`,
      })
      return false
    } else {
      notify({
        type: NotificationType.Success,
        silent: false,
        message: error?.message ?? `${capitalize(name)} Successfully removed`,
      })
      return true
    }
  }

  return {
    removeInstructionsFromProduct,
  }
}

export default useRemoveInstructionsFromProduct
